import { WorkExperienceCompany } from '@applicator/shared'
import React from 'react'



import { companyNameEditorClass } from './editorTypography'
import { getSaveHint } from './utils'
import WorkExperienceDragHandle from './WorkExperienceDragHandle'
import WorkExperienceRoleItem, { WorkExperienceRoleItemProps } from './WorkExperienceRoleItem'

interface WorkExperienceCompanyItemProps {
  company: WorkExperienceCompany
  editingCompanyId: string | null
  editingCompanyDescId: string | null
  editingCompanyName: string
  editingCompanyDesc: string
  hoveredCompanyId: string | null
  hoveredCompanyDescId: string | null
  companyDescTextareaRef: React.RefObject<HTMLTextAreaElement>
  highlightedRoleId: string | null
  highlightedBulletId?: string | null
  highlightedCompanyId?: string | null
  onStartEditCompany: (company: WorkExperienceCompany) => void
  onSaveCompany: (companyId: string) => void
  onStartEditCompanyDesc: (company: WorkExperienceCompany) => void
  onSaveCompanyDesc: (companyId: string) => void
  onDeleteCompany: (companyId: string) => void
  onToggleCompany: (companyId: string) => void
  onToggleCompanyDescription: (companyId: string) => void
  onAddRole: (companyId: string) => void
  onCompanyDescChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  adjustTextareaHeight: (textarea: HTMLTextAreaElement) => void
  renderCheckbox: (checked: boolean, onChange: () => void, size?: 'sm' | 'md') => React.ReactNode
  setHoveredCompanyId: (id: string | null) => void
  setHoveredCompanyDescId: (id: string | null) => void
  setEditingCompanyName: (value: string) => void
  setEditingCompanyId: (id: string | null) => void
  setEditingCompanyDescId: (id: string | null) => void
  setEditingCompanyDesc: (value: string) => void
  // Role props - all props from WorkExperienceRoleItemProps except company, role, highlightedRoleId
  roleProps: Omit<WorkExperienceRoleItemProps, 'company' | 'role' | 'highlightedRoleId'>
  draggedRoleId: string | null
  draggedRoleCompanyId: string | null
  dragOverRoleIndex: number | null
  setDraggedRoleId: (id: string | null) => void
  setDraggedRoleCompanyId: (id: string | null) => void
  setDragOverRoleIndex: (index: number | null) => void
  onReorderRoles: (companyId: string, draggedRoleId: string, targetIndex: number) => void
  clearRoleDrag: () => void
  companyIndex: number
  draggedCompanyId: string | null
  dragOverCompanyIndex: number | null
  setDraggedCompanyId: (id: string | null) => void
  setDragOverCompanyIndex: (index: number | null) => void
  onReorderCompanies: (draggedCompanyId: string, targetIndex: number) => void
  clearCompanyDrag: () => void
}

export default function WorkExperienceCompanyItem({
  company,
  editingCompanyId,
  editingCompanyDescId,
  editingCompanyName,
  editingCompanyDesc,
  hoveredCompanyId,
  hoveredCompanyDescId,
  companyDescTextareaRef,
  highlightedRoleId,
  highlightedBulletId,
  highlightedCompanyId,
  onStartEditCompany,
  onSaveCompany,
  onStartEditCompanyDesc,
  onSaveCompanyDesc,
  onDeleteCompany,
  onToggleCompany,
  onToggleCompanyDescription,
  onAddRole,
  onCompanyDescChange,
  adjustTextareaHeight,
  renderCheckbox,
  setHoveredCompanyId,
  setHoveredCompanyDescId,
  setEditingCompanyName,
  setEditingCompanyId,
  setEditingCompanyDescId,
  setEditingCompanyDesc,
  roleProps,
  draggedRoleId,
  draggedRoleCompanyId,
  dragOverRoleIndex,
  setDraggedRoleId,
  setDraggedRoleCompanyId,
  setDragOverRoleIndex,
  onReorderRoles,
  clearRoleDrag,
  companyIndex,
  draggedCompanyId,
  dragOverCompanyIndex,
  setDraggedCompanyId,
  setDragOverCompanyIndex,
  onReorderCompanies,
  clearCompanyDrag,
}: WorkExperienceCompanyItemProps) {
  const isCompanyDragging = draggedCompanyId === company.id
  const isCompanyDragOver =
    dragOverCompanyIndex === companyIndex && draggedCompanyId !== company.id

  return (
    <div 
      data-company-id={company.id}
      className="space-y-2 relative"
    >
      {/* Company header + description — draggable block only (not roles/bullets) */}
      <div
        data-wx-company-block="true"
        draggable
        onDragStart={(e) => {
          setDraggedCompanyId(company.id)
          e.dataTransfer.effectAllowed = 'move'
          e.dataTransfer.setData('text/plain', '')
        }}
        onDragOver={(e) => {
          e.preventDefault()
          e.dataTransfer.dropEffect = 'move'
          if (draggedCompanyId && draggedCompanyId !== company.id) {
            setDragOverCompanyIndex(companyIndex)
          }
        }}
        onDragLeave={() => {
          if (dragOverCompanyIndex === companyIndex) {
            setDragOverCompanyIndex(null)
          }
        }}
        onDrop={(e) => {
          e.preventDefault()
          if (draggedCompanyId && draggedCompanyId !== company.id) {
            onReorderCompanies(draggedCompanyId, companyIndex)
          }
          clearCompanyDrag()
        }}
        onDragEnd={clearCompanyDrag}
        className={`group/wx-company flex items-start gap-2 rounded-lg cursor-move transition-all ${
          isCompanyDragging ? 'opacity-50' : ''
        } ${isCompanyDragOver ? 'ring-2 ring-interactive-focus-ring ring-inset' : ''} ${
          highlightedCompanyId === company.id
            ? 'ring-2 ring-interactive-focus-ring ring-opacity-50'
            : ''
        }`}
      >
        <WorkExperienceDragHandle
          group="wx-company"
          title="Перетащите компанию для изменения порядка"
        />
        <div className="flex-1 min-w-0 space-y-2">
      {/* Company Name */}
      <div 
        className="flex items-center gap-3"
        onMouseEnter={() => setHoveredCompanyId(company.id)}
        onMouseLeave={() => setHoveredCompanyId(null)}
      >
        <div 
          className={`flex items-center gap-3 flex-1 rounded transition-colors ${
            hoveredCompanyId === company.id ? 'bg-surface-muted' : ''
          }`}
        >
          {renderCheckbox(
            company.included,
            () => onToggleCompany(company.id),
            'xs'
          )}
          <div className="flex-1">
            {editingCompanyId === company.id ? (
              <input
                type="text"
                value={editingCompanyName}
                onChange={(e) => setEditingCompanyName(e.target.value)}
                onBlur={() => onSaveCompany(company.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    onSaveCompany(company.id)
                  } else if (e.key === 'Escape') {
                    e.preventDefault()
                    setEditingCompanyId(null)
                    setEditingCompanyName('')
                  }
                }}
                placeholder="Company name"
                className="w-full px-2 py-1 text-sm border border-interactive rounded focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
            ) : (
              <div
                role="button"
                tabIndex={0}
                onClick={() => onStartEditCompany(company)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onStartEditCompany(company)
                  }
                }}
                className="cursor-text rounded p-1 -m-1"
              >
                <div className={`text-sm ${companyNameEditorClass(company)}`}>
                  {company.name || 'Company name'}
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Delete Company Button */}
        <button
          onClick={(e) => {
            e.stopPropagation()
            onDeleteCompany(company.id)
          }}
          className={`p-1.5 text-text-muted hover:text-danger transition-opacity ${
            hoveredCompanyId === company.id ? 'opacity-100' : 'opacity-0'
          }`}
          title="Delete company"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>

      {/* Company Description */}
      <div className="flex items-start gap-3">
        <div 
          className={`flex items-start gap-3 flex-1 rounded transition-colors ${
            hoveredCompanyDescId === company.id ? 'bg-surface-muted' : ''
          }`}
          onMouseEnter={() => {
            setHoveredCompanyDescId(company.id)
            setHoveredCompanyId(null) // Clear company name hover when hovering over description
          }}
          onMouseLeave={() => setHoveredCompanyDescId(null)}
        >
          <div className="pt-0.5">
            {renderCheckbox(
              company.descriptionIncluded ?? true,
              () => onToggleCompanyDescription(company.id),
              'xs'
            )}
          </div>
          <div className="flex-1 min-w-0 relative">
            {editingCompanyDescId === company.id ? (
              <>
                <textarea
                  ref={(el) => {
                    companyDescTextareaRef.current = el
                    if (el) {
                      // Adjust height when ref is set
                      setTimeout(() => adjustTextareaHeight(el), 0)
                    }
                  }}
                  value={editingCompanyDesc}
                  onChange={onCompanyDescChange}
                  onBlur={() => onSaveCompanyDesc(company.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault()
                      onSaveCompanyDesc(company.id)
                    } else if (e.key === 'Escape') {
                      e.preventDefault()
                      setEditingCompanyDescId(null)
                      setEditingCompanyDesc('')
                    }
                  }}
                  onFocus={(e) => adjustTextareaHeight(e.target)}
                  placeholder="Company description"
                  rows={1}
                  className="w-full px-2 py-1 pb-5 text-sm border border-interactive rounded focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring resize-none overflow-hidden leading-relaxed"
                  style={{ minHeight: '2.5rem' }}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
                <div className="absolute bottom-1 right-2 text-xs text-text-muted pointer-events-none">
                  {getSaveHint()}
                </div>
              </>
            ) : (
              <div
                role="button"
                tabIndex={0}
                onClick={() => onStartEditCompanyDesc(company)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onStartEditCompanyDesc(company)
                  }
                }}
                className="cursor-text rounded p-1 -m-1"
              >
                {company.description ? (
                  <div className="text-xs text-text-secondary leading-relaxed whitespace-pre-wrap">{company.description}</div>
                ) : (
                  <div className="text-xs text-text-muted italic leading-relaxed">Click to add company description...</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
        </div>
      </div>
          
      {/* Roles — header draggable; bullets separate */}
      {(company.roles || []).map((role, roleIndex) => {
        const isRoleDragging =
          draggedRoleId === role.id && draggedRoleCompanyId === company.id
        const isRoleDragOver =
          dragOverRoleIndex === roleIndex &&
          draggedRoleCompanyId === company.id &&
          draggedRoleId !== role.id

        return (
          <div
            key={`${role.id}-${roleIndex}`}
            className={`ml-6 space-y-2 ${
              highlightedRoleId === role.id
                ? 'bg-brand-primary-subtle ring-2 ring-interactive-focus-ring ring-opacity-50 rounded-lg p-2 -m-2'
                : ''
            }`}
          >
            <div
              data-wx-role-block="true"
              data-role-id={role.id}
              draggable
              onDragStart={(e) => {
                setDraggedRoleCompanyId(company.id)
                setDraggedRoleId(role.id)
                e.dataTransfer.effectAllowed = 'move'
                e.dataTransfer.setData('text/plain', '')
              }}
              onDragOver={(e) => {
                e.preventDefault()
                e.dataTransfer.dropEffect = 'move'
                if (draggedRoleCompanyId === company.id && draggedRoleId !== role.id) {
                  setDragOverRoleIndex(roleIndex)
                }
              }}
              onDragLeave={() => {
                if (dragOverRoleIndex === roleIndex) {
                  setDragOverRoleIndex(null)
                }
              }}
              onDrop={(e) => {
                e.preventDefault()
                if (
                  draggedRoleId &&
                  draggedRoleCompanyId === company.id &&
                  draggedRoleId !== role.id
                ) {
                  onReorderRoles(company.id, draggedRoleId, roleIndex)
                }
                clearRoleDrag()
              }}
              onDragEnd={clearRoleDrag}
              className={`group/wx-role cursor-move rounded transition-all ${
                isRoleDragging ? 'opacity-50' : ''
              } ${isRoleDragOver ? 'ring-2 ring-interactive-focus-ring ring-inset' : ''}`}
            >
              <div className="flex items-start gap-2">
                <WorkExperienceDragHandle group="wx-role" />
                <div className="flex-1 min-w-0">
                  <WorkExperienceRoleItem
                    company={company}
                    role={role}
                    section="header"
                    highlightedRoleId={highlightedRoleId}
                    highlightedBulletId={highlightedBulletId}
                    {...roleProps}
                  />
                </div>
              </div>
            </div>
            <WorkExperienceRoleItem
              company={company}
              role={role}
              section="bullets"
              highlightedRoleId={highlightedRoleId}
              highlightedBulletId={highlightedBulletId}
              {...roleProps}
            />
          </div>
        )
      })}
      <button
        onClick={() => onAddRole(company.id)}
        className="ml-6 text-xs text-interactive hover:text-interactive font-medium"
      >
        + Add Role
      </button>
    </div>
  )
}
